/**
 * Reading a registered lifecycle, once (#1674).
 *
 * ── the defect this file is ────────────────────────────────────────────────
 * #1581 taught the console to stop holding a local copy of a lifecycle and read
 * `core.status_machine` instead. Four desks then converted — service, feedback
 * triage, contracts, commerce — and each one wrote the same two helpers again:
 *
 *     const TONE_OF = { good, warn, critical, brand, info: 'brand', neutral }
 *     function stateOf(states, code) { ... ?? code ... 'neutral' }
 *     function legalFrom(machine, from) { ... }
 *
 * Which is the shape #1581 was about, one level up: four copies of the rule for
 * reading the single source. And they had already drifted in the way that
 * matters — all four ended `?? 'neutral'` over a six-word table, while the
 * seeds use nine words, so `bad`, `success` and `muted` rendered grey. See
 * `dbTone` in `@solum/ui`, and the census that reds when a tenth appears.
 *
 * ── the two questions a desk actually asks ─────────────────────────────────
 * What is this state CALLED, and which moves are LEGAL from it. Both are
 * answered by the machine and by nothing else. A desk still owns the VERB on a
 * button — "Mark paid" is a word for an act, and the machine has no vocabulary
 * for it — which is why no label map lives here.
 */
import { dbTone, type Tone } from '@solum/ui'
import type { StatusState } from '../data/queries'

/** A machine as the desks consume it: the parts `describe_status_machine` returns. */
export interface StatusMachineShape {
  states?: StatusState[]
  transitions?: { from: string; to: string }[]
}

/**
 * What a state is called and how it is drawn.
 *
 * Falls back to the raw code rather than to an empty pill: a state the read has
 * not answered for yet is better shown as `awaiting_signature` than as nothing,
 * and it is the shape a reader can report.
 */
export function stateOf(states: StatusState[] | undefined, code: string): { label: string; tone: Tone } {
  const st = (states ?? []).find((x) => x.code === code)
  return { label: st?.label ?? code, tone: dbTone(st?.tone) }
}

/**
 * The states the machine declares reachable from `from`.
 *
 * Empty when the read has not resolved — deliberately, and it is the whole
 * point: an action the machine has not confirmed is exactly the button #1581
 * was about, so a desk that offers nothing while it waits is telling the truth.
 */
export function legalFrom(machine: StatusMachineShape | null | undefined, from: string): Set<string> {
  return new Set((machine?.transitions ?? []).filter((t) => t.from === from).map((t) => t.to))
}

/**
 * The categories that mean a lifecycle is OVER.
 *
 * Platform-owned, not tenant-renamable: `core.status_state.code` is renamable
 * and `category` is the half “platform/module-owned (logic branches on it)”
 * (`0046_status_machine.sql:23-29`). It lives here rather than beside its first
 * caller because a second reader arrived (#1736 asks a flow definition whether
 * any stage ENDS it), and two copies of a closed vocabulary agree until
 * somebody adds a word to one of them.
 */
export const OUTCOME_CATEGORY = new Set(['done', 'closed', 'refused'])
